import { useRef } from 'react';

import { XYChartSynchronizedCrosshair } from './XYChartSynchronizedCrosshair.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { InteractionsOverlay } from '../../../core/components/interactions-overlay/InteractionsOverlay.js';
import { reduceExploreCancelAll } from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { XYChartOverlayRefContext } from '../../context/XYChartOverlayRefContext.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import {
  useInteractionModeState,
  usePinnedState,
  useSelectState,
  useSetState,
  useVisibleState,
} from '../../store/selectors.js';
import { XYChartToolbar } from '../toolbar/XYChartToolbar.js';
import { XYChartTooltip } from '../tooltip/XYChartTooltip.js';
import { XYChartCrosshair } from './explore/XYChartCrosshair.js';
import { XYChartDotPoint } from './explore/XYChartDotPoint.js';
import { XYChartRectPoint } from './explore/XYChartRectPoint.js';
import { useOverlayEvents } from './hooks/useOverlayEvents.js';
import { useResetOnResize } from './hooks/useResetOnResize.js';
import { XYChartSelectionArea } from './XYChartSelectionArea.js';

export const XYChartOverlay = () => {
  const setState = useSetState();
  const chartRef = useRef<HTMLDivElement>(null);
  const { width, height } = useGridPlotSize();
  const visible = useVisibleState();
  const pinned = usePinnedState();
  const {
    handleModeChange,
    handleHover,
    handleTogglePin,
    handleReset,
    handleLeave,
    handlePanChange,
    handleFocus,
    handleSelectChange,
    handleSelectEnd,
    handleZoomChange,
  } = useOverlayEvents();
  const mode = useInteractionModeState();
  const selectState = useSelectState();
  const { axesMetadata } = useXYChartData();

  useResetOnResize();

  return (
    <InteractionsOverlay
      ref={chartRef}
      mode={mode}
      pinned={pinned}
      selectionArea={
        selectState ? [selectState?.start, selectState?.end] : undefined
      }
      data-testid="xy-chart-overlay"
      width={width}
      height={height}
      onModeChange={handleModeChange}
      onReset={handleReset}
      onLeave={handleLeave}
      onPanChange={handlePanChange}
      onHover={handleHover}
      onFocus={handleFocus}
      onTogglePin={({ pin, position }) => handleTogglePin(pin, position)}
      onSelectChange={handleSelectChange}
      onSelectEnd={handleSelectEnd}
      onZoomChange={handleZoomChange}
    >
      <XYChartOverlayRefContext.Provider value={chartRef}>
        {(visible || pinned) && (
          <>
            <XYChartTooltip key="XYChartTooltip" />
            <XYChartCrosshair
              key="XYChartCrosshair"
              chartRef={chartRef}
              pinned={pinned}
            />
            <XYChartDotPoint key="XYChartDotPoint" />
            <XYChartRectPoint key="XYChartRectPoint" />
          </>
        )}

        <XYChartSynchronizedCrosshair />
        <XYChartToolbar onMouseEnter={() => setState(reduceExploreCancelAll)} />
        {axesMetadata.isZoomEnabled && <XYChartSelectionArea />}
      </XYChartOverlayRefContext.Provider>
    </InteractionsOverlay>
  );
};

XYChartOverlay['displayName'] = 'XYChartOverlay';
